/**
 * useMe — hook unique pour lire l'identité de l'utilisateur courant.
 *
 * Source de vérité = `trpc.auth.me`. React Query déduplique les appels :
 * tous les composants qui consomment `useMe()` partagent le même cache via
 * la queryKey `["auth.me"]`. Pour invalider après login/logout :
 *
 *   queryClient.invalidateQueries({ queryKey: ["auth.me"] });
 *
 * Le hook ne redirige jamais — c'est `<RequireAuth>` qui décide. Cela
 * permet d'utiliser `useMe()` sur des écrans publics (ex : Topbar partagée
 * entre app et écran de transition) sans déclencher de redirect intempestif.
 */

import { onlineManager, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSyncExternalStore, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { buildNextParam } from "../lib/safe-next.js";
import { trpc } from "../lib/trpc.js";

import { identiteSessionUtilisable, refusAccesSession } from "./disponibilite-session.js";
import { EtatConnexionSession } from "./EtatConnexionSession.js";

/** Clé partagée — exportée pour invalidation depuis les pages auth. */
export const ME_QUERY_KEY = ["auth.me"] as const;

export function useMe() {
  return useQuery({
    queryKey: ME_QUERY_KEY,
    queryFn: () => trpc.auth.me.query(),
    // Ne pas retarder la fermeture sur un vrai refus d'accès.
    retry: (failureCount, error) => {
      if (refusAccesSession(error)) return false;
      return failureCount < 1;
    },
    // `me` est appelé sur quasi tous les écrans authed → garde-le frais
    // raisonnablement, mais sans hammer le backend.
    staleTime: 1000 * 60, // 1 min
    // Polling léger : permet de détecter une session révoquée côté backend
    // (suspension par admin, exclusion, expiration absolue) MÊME quand l'user
    // ne touche pas l'écran. Au prochain tick, le serveur renvoie 401, `retry`
    // refuse l'erreur → RequireAuth bascule en isError → redirect /login.
    // 60s = compromis entre réactivité (admin suspend, max 60s avant éjection)
    // et coût serveur (1 appel /me par tab actif et par minute). Précédemment
    // à 30s — divisé par 2 post-revue performance B.2 (charge idle non
    // négligeable à 50+ users × 2 onglets). `refetchOnWindowFocus` couvre
    // déjà le retour sur l'onglet, donc 60s est largement suffisant.
    refetchInterval: 60_000,
    // Pas en arrière-plan (tab masqué) : économie batterie/réseau quand l'user
    // n'est pas sur le tab. La détection reprendra dès qu'il y revient (focus
    // refetch est déjà actif par défaut React Query).
    refetchIntervalInBackground: false,
  });
}

/**
 * Hook utilitaire pour invalider le cache `me` (à appeler après login/logout
 * réussi pour forcer un refetch immédiat).
 */
export function useInvalidateMe() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
}

/**
 * Garde de route — bloque l'accès aux écrans authed tant que l'identité
 * n'est pas confirmée. Comportement :
 *  - chargement initial → écran d'attente discret (pas de spinner agressif)
 *  - 401/403 → bloque, sans repli sur l'identité en cache
 *  - panne → garde seulement l'identité déjà confirmée, sans démonter la saisie
 *  - sans identité → écran indisponible, jamais de données sensibles
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const location = useLocation();
  const session = useMe();
  const enLigne = useSyncExternalStore(souscrireReseau, lireReseau, () => true);
  const refus = refusAccesSession(session.error);

  if (refus) {
    const next = buildNextParam(location);
    return (
      <Navigate
        to={refus === "UNAUTHORIZED" ? `/login?next=${encodeURIComponent(next)}` : "/login"}
        replace
      />
    );
  }

  if (session.isLoading && enLigne && !session.data) {
    // Volontairement minimal : un flash blanc qui dure ~50ms en local. Si on
    // pose une animation ici, on la voit à chaque navigation et c'est plus
    // gênant que rien.
    return <div className="dashboard-bg h-screen w-screen" aria-hidden="true" />;
  }

  const identiteConfirmee = identiteSessionUtilisable(session);
  const connexionIncertaine = !enLigne || session.isPaused || session.isError;
  return (
    <div className="flex h-dvh min-w-0 flex-col overflow-x-hidden">
      {(connexionIncertaine || !identiteConfirmee) && (
        <EtatConnexionSession
          identiteConfirmee={identiteConfirmee}
          horsLigne={!enLigne || session.isPaused}
          verificationEnCours={session.isFetching}
          reessayer={() => void session.refetch()}
        />
      )}
      {/* Emplacement et clé stables pendant une panne. Un changement réel
          d'identité/tenant doit au contraire détruire les anciens brouillons. */}
      <div
        className="min-h-0 min-w-0 flex-1"
        key={`${session.data?.utilisateur?.id ?? "sans-identite"}:${session.data?.membre_id_actif ?? "sans-membre"}:${session.data?.domaine_id_actif ?? "sans-domaine"}`}
      >
        {identiteConfirmee ? children : null}
      </div>
    </div>
  );
}

const souscrireReseau = (notifier: () => void) => onlineManager.subscribe(notifier);
const lireReseau = () => onlineManager.isOnline();
